import { useEffect, useState } from "react";
import { useAutoRefresh } from "../lib/autoRefresh";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../api";
import { PageHeader, Badge, Btn, eur } from "../components/common";
import { useAuth } from "../auth";

const PERIODO_LABEL = { anual: "Anual", "3anos": "3 anos", "5anos": "5 anos" };
const PERIODO_ORDER = ["anual", "3anos", "5anos"];
const round2 = (v) => Math.round(v * 100) / 100;

const EMPTY_PLAN = {
  plataforma_id: "", nome: "", sku: "",
  preco_mensal_1ano: 0, preco_mensal_3anos: 0, preco_mensal_5anos: 0,
  desconto_3anos: 10, desconto_5anos: 20,
  preco_anual: 0, preco_3anos: 0, preco_5anos: 0,
  periodos_disponiveis: ["anual"],
  moeda: "EUR", taxa_iva_regiao: 23, setup_fee: 0, trial_dias: 0,
  limites: "", ativo: true,
  max_utilizadores: 0,
  addon_triagem_mensal: 0, addon_dpo_mensal: 0,
  max_denuncias: -1, valor_extra_denuncia: 0, max_titulares: -1, max_tratamentos: -1,
};

export default function Catalog() {
  const { can } = useAuth();
  const canWrite = can("*");
  const [platforms, setPlatforms] = useState([]);
  const [plans, setPlans] = useState([]);
  const [edit, setEdit] = useState(null);

  const load = () => {
    api.get("/platforms").then((r) => setPlatforms(r.data));
    api.get("/plans").then((r) => setPlans(r.data));
  };
  useEffect(load, []);
  useAutoRefresh(load);

  const remove = async (r) => {
    if (!confirm(`Eliminar plano "${r.nome}"?`)) return;
    try { await api.delete(`/plans/${r.id}`); toast.success("Plano eliminado"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader
        overline="Catalogo"
        title="Plataformas e planos"
        action={canWrite && <Btn onClick={() => setEdit({ ...EMPTY_PLAN, plataforma_id: platforms[0]?.id || "" })} testid="new-plan-btn"><Plus size={16} /> Novo plano</Btn>}
      />
      <div className="space-y-8 mb-10">
        {platforms.map((p) => {
          const platPlans = plans.filter((pl) => pl.plataforma_id === p.id);
          return (
            <div key={p.id} className="border border-tint rounded-xl bg-white overflow-hidden" data-testid={`platform-${p.slug}`}>
              <div className="bg-slate-50 border-b border-tint px-6 py-4 flex items-center justify-between">
                <div>
                  <h3 className="text-navy font-bold text-lg tracking-tight">{p.nome}</h3>
                  <p className="text-muted2 text-sm leading-relaxed mt-0.5">{p.descricao}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge value={p.ativo ? "ativo" : "inativo"} />
                  <span className="text-xs bg-blue/10 text-blue px-2 py-0.5 rounded-full font-semibold">
                    {platPlans.length} plano{platPlans.length !== 1 ? "s" : ""}
                  </span>
                </div>
              </div>
              {platPlans.length === 0 ? (
                <div className="px-6 py-4 text-sm text-muted2">Sem planos configurados para esta plataforma.</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-6">
                  {platPlans.map((plan) => (
                    <div key={plan.id} className="border border-tint rounded-lg p-4 hover:border-blue/30 transition-colors" data-testid={`catalog-plan-${plan.sku}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-navy text-sm">{plan.nome}</span>
                        <Badge value={plan.ativo ? "ativo" : "inativo"} />
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <code className="text-[10px] bg-tint px-2 py-0.5 rounded text-muted2">{plan.sku}</code>
                        {plan.max_utilizadores > 0 && (
                          <span className="text-[10px] bg-navy/10 text-navy px-2 py-0.5 rounded-full font-semibold">
                            {plan.max_utilizadores} utilizador{plan.max_utilizadores !== 1 ? "es" : ""}
                          </span>
                        )}
                      </div>
                      <div className="mt-3 space-y-1">
                        {(plan.periodos_disponiveis || []).map((per) => {
                          const mensalMap = { anual: plan.preco_mensal_1ano, "3anos": plan.preco_mensal_3anos, "5anos": plan.preco_mensal_5anos };
                          const totalMap = { anual: plan.preco_anual, "3anos": plan.preco_3anos, "5anos": plan.preco_5anos };
                          const mensal = mensalMap[per] || 0;
                          const total = totalMap[per] || 0;
                          if (!mensal && !total) return null;
                          return (
                            <div key={per} className="flex items-center justify-between text-xs">
                              <span className="text-muted2">{PERIODO_LABEL[per] || per}</span>
                              <span>
                                <b className="text-navy">{eur(mensal)}/mes</b>
                                <span className="text-muted2 ml-1">({eur(total)} total)</span>
                              </span>
                            </div>
                          );
                        })}
                      </div>
                      {(plan.addon_triagem_mensal > 0 || plan.addon_dpo_mensal > 0) && (
                        <div className="mt-2 pt-2 border-t border-dashed border-tint space-y-1">
                          <span className="text-[10px] uppercase font-semibold text-muted2 tracking-wide">Add-ons servico interno</span>
                          {plan.addon_triagem_mensal > 0 && (
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-muted2">Gestao Triagem</span>
                              <b className="text-emerald-700">{eur(plan.addon_triagem_mensal)}/mes</b>
                            </div>
                          )}
                          {plan.addon_dpo_mensal > 0 && (
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-muted2">DPO as a Service</span>
                              <b className="text-emerald-700">{eur(plan.addon_dpo_mensal)}/mes</b>
                            </div>
                          )}
                        </div>
                      )}
                      {(plan.max_denuncias != null && plan.max_denuncias !== -1) && (
                        <div className="mt-2 pt-2 border-t border-dashed border-tint space-y-1">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-muted2">Denuncias/mes</span>
                            <b className="text-navy">{plan.max_denuncias}</b>
                          </div>
                          {plan.valor_extra_denuncia > 0 && (
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-muted2">Extra/denuncia</span>
                              <b className="text-amber-700">{eur(plan.valor_extra_denuncia)}/un</b>
                            </div>
                          )}
                        </div>
                      )}
                      {(plan.max_titulares != null && plan.max_titulares !== -1) && (
                        <div className="mt-2 pt-2 border-t border-dashed border-tint">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-muted2">Max titulares</span>
                            <b className="text-navy">{plan.max_titulares}</b>
                          </div>
                        </div>
                      )}
                      {canWrite && (
                        <div className="flex gap-1 mt-3 pt-2 border-t border-tint">
                          <button onClick={() => setEdit({ ...plan })} data-testid={`plan-edit-${plan.sku}`}
                            className="text-xs px-2 py-1 rounded bg-tint text-navy hover:bg-blue hover:text-white inline-flex items-center gap-1">
                            <Pencil size={11} /> Editar
                          </button>
                          <button onClick={() => remove(plan)} data-testid={`plan-delete-${plan.sku}`}
                            className="text-xs px-2 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50">
                            <Trash2 size={11} />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {edit && <PlanModal plan={edit} platforms={platforms} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function PlanModal({ plan, platforms, onClose, onSaved }) {
  const isNew = !plan.id;
  const [form, setForm] = useState({
    ...EMPTY_PLAN,
    ...plan,
    periodos_disponiveis: (plan.periodos_disponiveis?.length ? plan.periodos_disponiveis : (plan.periodo ? [plan.periodo] : ["anual"])).filter((p) => p !== "mensal"),
  });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm({ ...form, [k]: v });
  const toggle = (p) => {
    const cur = new Set(form.periodos_disponiveis || []);
    cur.has(p) ? cur.delete(p) : cur.add(p);
    setForm({ ...form, periodos_disponiveis: Array.from(cur) });
  };

  const base = Number(form.preco_mensal_1ano) || 0;
  const calc3 = base > 0 ? round2(base * (1 - (Number(form.desconto_3anos) || 0) / 100)) : 0;
  const calc5 = base > 0 ? round2(base * (1 - (Number(form.desconto_5anos) || 0) / 100)) : 0;

  const updBase = (v) => {
    const b = Number(v) || 0;
    setForm((f) => ({
      ...f,
      preco_mensal_1ano: v,
      preco_mensal_3anos: round2(b * (1 - (Number(f.desconto_3anos) || 0) / 100)),
      preco_mensal_5anos: round2(b * (1 - (Number(f.desconto_5anos) || 0) / 100)),
    }));
  };
  const updDesc = (key, v) => {
    const pct = Number(v) || 0;
    setForm((f) => {
      const b = Number(f.preco_mensal_1ano) || 0;
      const mensalKey = key === "desconto_3anos" ? "preco_mensal_3anos" : "preco_mensal_5anos";
      return { ...f, [key]: v, [mensalKey]: round2(b * (1 - pct / 100)) };
    });
  };

  const save = async () => {
    if (!form.nome.trim() || !form.sku.trim() || !form.plataforma_id) return toast.error("Preencha nome, SKU e plataforma.");
    if ((form.periodos_disponiveis || []).length === 0) return toast.error("Selecione pelo menos um periodo de compromisso.");
    if (base <= 0 && form.periodos_disponiveis?.includes("anual")) return toast.error("Defina o preco base mensal (1 ano).");
    setBusy(true);
    try {
      const m1 = Number(form.preco_mensal_1ano) || 0;
      const m3 = calc3;
      const m5 = calc5;
      const body = {
        ...form,
        preco_mensal_1ano: m1,
        preco_mensal_3anos: m3,
        preco_mensal_5anos: m5,
        desconto_3anos: Number(form.desconto_3anos) || 0,
        desconto_5anos: Number(form.desconto_5anos) || 0,
        preco: m1 * 12,
        preco_anual: m1 * 12,
        preco_3anos: m3 * 36,
        preco_5anos: m5 * 60,
        taxa_iva_regiao: Number(form.taxa_iva_regiao || 23),
        setup_fee: Number(form.setup_fee || 0),
        trial_dias: Number(form.trial_dias || 0),
        max_utilizadores: Number(form.max_utilizadores || 0),
        addon_triagem_mensal: Number(form.addon_triagem_mensal || 0),
        addon_dpo_mensal: Number(form.addon_dpo_mensal || 0),
        max_denuncias: Number(form.max_denuncias ?? -1),
        valor_extra_denuncia: Number(form.valor_extra_denuncia || 0),
        max_titulares: Number(form.max_titulares ?? -1),
        max_tratamentos: Number(form.max_tratamentos ?? -1),
      };
      if (isNew) await api.post("/plans", body);
      else await api.put(`/plans/${plan.id}`, body);
      toast.success("Plano guardado");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="plan-modal">
      <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-navy text-xl font-bold tracking-tight">{isNew ? "Novo plano" : `Editar ${form.nome}`}</h2>
          <button onClick={onClose}><X className="text-muted2" /></button>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-xs font-semibold text-navy mb-1">Plataforma</label>
            <select value={form.plataforma_id} onChange={(e) => upd("plataforma_id", e.target.value)} data-testid="plan-plataforma" className="w-full px-3 py-2 border border-tint rounded-lg text-sm">
              <option value="">-</option>
              {platforms.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-navy mb-1">Estado</label>
            <select value={form.ativo ? "1" : "0"} onChange={(e) => upd("ativo", e.target.value === "1")} className="w-full px-3 py-2 border border-tint rounded-lg text-sm">
              <option value="1">Ativo</option><option value="0">Inativo</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-xs font-semibold text-navy mb-1">Nome</label>
            <input value={form.nome} onChange={(e) => upd("nome", e.target.value)} data-testid="plan-nome" className="w-full px-3 py-2 border border-tint rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-navy mb-1">SKU</label>
            <input value={form.sku} onChange={(e) => upd("sku", e.target.value)} data-testid="plan-sku" className="w-full px-3 py-2 border border-tint rounded-lg text-sm" />
          </div>
        </div>

        <div className="border border-tint rounded-lg p-3 mb-3 bg-slate-50/60">
          <div className="text-xs font-semibold text-navy mb-2">Compromissos disponiveis (o cliente escolhe na loja/proposta)</div>
          <div className="flex flex-wrap gap-3">
            {PERIODO_ORDER.map((p) => (
              <label key={p} className="inline-flex items-center gap-1.5 text-sm">
                <input type="checkbox" checked={form.periodos_disponiveis?.includes(p) || false} onChange={() => toggle(p)} data-testid={`plan-period-${p}`} />
                {PERIODO_LABEL[p]}
              </label>
            ))}
          </div>
        </div>

        <div className="border border-tint rounded-lg p-3 mb-3 bg-slate-50/60">
          <div className="text-xs font-semibold text-navy mb-3">Precos mensais por compromisso</div>
          <div className="space-y-3">
            <div className={form.periodos_disponiveis?.includes("anual") ? "" : "opacity-40"}>
              <label className="block text-[10px] font-semibold text-navy uppercase tracking-wide mb-1">Preco base mensal (1 ano)</label>
              <input type="number" value={form.preco_mensal_1ano} onChange={(e) => updBase(e.target.value)} data-testid="plan-preco_mensal_1ano"
                className="w-full px-2 py-1.5 border border-tint rounded text-sm" step="0.01" placeholder="0.00" />
              {base > 0 && <div className="text-[10px] text-muted2 mt-0.5">Total anual: {eur(base * 12)}</div>}
            </div>
            <div className={`grid grid-cols-2 gap-3 ${form.periodos_disponiveis?.includes("3anos") ? "" : "opacity-40"}`}>
              <div>
                <label className="block text-[10px] font-semibold text-navy uppercase tracking-wide mb-1">Desconto 3 anos (%)</label>
                <input type="number" value={form.desconto_3anos} onChange={(e) => updDesc("desconto_3anos", e.target.value)} data-testid="plan-desconto-3anos"
                  className="w-full px-2 py-1.5 border border-tint rounded text-sm" step="0.5" min="0" max="100" />
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-navy uppercase tracking-wide mb-1">Mensal (3 anos)</label>
                <div className="px-2 py-1.5 border border-tint rounded text-sm bg-white text-navy font-semibold" data-testid="plan-preco_mensal_3anos_calc">
                  {eur(calc3)}/mes
                </div>
                <div className="text-[10px] text-muted2 mt-0.5">Total 3 anos: {eur(calc3 * 36)}</div>
              </div>
            </div>
            <div className={`grid grid-cols-2 gap-3 ${form.periodos_disponiveis?.includes("5anos") ? "" : "opacity-40"}`}>
              <div>
                <label className="block text-[10px] font-semibold text-navy uppercase tracking-wide mb-1">Desconto 5 anos (%)</label>
                <input type="number" value={form.desconto_5anos} onChange={(e) => updDesc("desconto_5anos", e.target.value)} data-testid="plan-desconto-5anos"
                  className="w-full px-2 py-1.5 border border-tint rounded text-sm" step="0.5" min="0" max="100" />
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-navy uppercase tracking-wide mb-1">Mensal (5 anos)</label>
                <div className="px-2 py-1.5 border border-tint rounded text-sm bg-white text-navy font-semibold" data-testid="plan-preco_mensal_5anos_calc">
                  {eur(calc5)}/mes
                </div>
                <div className="text-[10px] text-muted2 mt-0.5">Total 5 anos: {eur(calc5 * 60)}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-3">
          <div>
            <label className="block text-xs font-semibold text-navy mb-1">Max utilizadores</label>
            <input type="number" value={form.max_utilizadores} onChange={(e) => upd("max_utilizadores", e.target.value)} data-testid="plan-max-utilizadores"
              className="w-full px-3 py-2 border border-tint rounded-lg text-sm" min="0" placeholder="0" />
            <div className="text-[10px] text-muted2 mt-0.5">Utilizador Cogniseg (triagem/DPO) nao conta</div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-navy mb-1">Max titulares</label>
            <input type="number" value={form.max_titulares ?? -1} onChange={(e) => upd("max_titulares", e.target.value)} data-testid="plan-max-titulares"
              className="w-full px-3 py-2 border border-tint rounded-lg text-sm" placeholder="-1" />
            <div className="text-[10px] text-muted2 mt-0.5">Privaxia (-1 = ilimitado)</div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-navy mb-1">Max tratamentos</label>
            <input type="number" value={form.max_tratamentos ?? -1} onChange={(e) => upd("max_tratamentos", e.target.value)} data-testid="plan-max-tratamentos"
              className="w-full px-3 py-2 border border-tint rounded-lg text-sm" placeholder="-1" />
            <div className="text-[10px] text-muted2 mt-0.5">Privaxia (-1 = ilimitado)</div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-xs font-semibold text-navy mb-1">Add-on Triagem/mes</label>
            <input type="number" value={form.addon_triagem_mensal} onChange={(e) => upd("addon_triagem_mensal", e.target.value)} data-testid="plan-addon-triagem"
              className="w-full px-3 py-2 border border-tint rounded-lg text-sm" step="0.01" placeholder="0.00" />
            <div className="text-[10px] text-muted2 mt-0.5">Gestao de Triagem (CogniEtica)</div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-navy mb-1">Add-on DPO/mes</label>
            <input type="number" value={form.addon_dpo_mensal} onChange={(e) => upd("addon_dpo_mensal", e.target.value)} data-testid="plan-addon-dpo"
              className="w-full px-3 py-2 border border-tint rounded-lg text-sm" step="0.01" placeholder="0.00" />
            <div className="text-[10px] text-muted2 mt-0.5">DPO as a Service (Privaxia)</div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-4">
          <div>
            <label className="block text-xs font-semibold text-navy mb-1">Moeda</label>
            <input value={form.moeda} onChange={(e) => upd("moeda", e.target.value.toUpperCase())} className="w-full px-3 py-2 border border-tint rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-navy mb-1">IVA (%)</label>
            <input type="number" value={form.taxa_iva_regiao} onChange={(e) => upd("taxa_iva_regiao", e.target.value)} className="w-full px-3 py-2 border border-tint rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-navy mb-1">Trial (dias)</label>
            <input type="number" value={form.trial_dias} onChange={(e) => upd("trial_dias", e.target.value)} className="w-full px-3 py-2 border border-tint rounded-lg text-sm" />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-tint">
          <Btn variant="ghost" onClick={onClose}>Cancelar</Btn>
          <Btn onClick={save} disabled={busy} testid="plan-save">{busy ? "..." : "Guardar plano"}</Btn>
        </div>
      </div>
    </div>
  );
}
